/**
 * Chat surface endpoints — sessions, messages, folders, streaming, tools.
 */

import { ApiError, extractError, get, post } from './_shared.ts';

// ---- Chat surface ----

/** Push #34: Anthropic-shape tool definition prefetched at session create. */
export interface ChatTool {
  name: string;
  description: string;
  input_schema: Record<string, unknown>;
}

/** Push #34: tool routing meta — permission_mode + cost per tool.
 *  Migration 0116 (Push #4.5 Item 1) collapsed permission_mode to 2 values
 *  (allow/disallow); 'ask' was dropped in favor of the per-user
 *  ConfirmCard architecture (Item 3 — gated by
 *  sys__account_user.confirm_writes_in_chat). The disallow rows are
 *  filtered out server-side at /internal/tools/list so the SPA only ever
 *  sees 'allow'. Union retained for legacy back-compat with older SPA
 *  builds. access_class is the Item 3 plumbing for the ConfirmCard gate. */
export interface ChatToolRouting {
  name: string;
  connector_instance_id: string;
  template_tool_name: string;
  permission_mode: 'allow' | 'disallow';
  cost_units: number;
  /** D-093 access_class. Optional for D-169 fallback compat; default
   *  treated as 'read' (no ConfirmCard pause). */
  access_class?: 'read' | 'write' | 'destructive';
}

export interface ChatSuggestedPrompt {
  id: string;
  name: string;
  display_name: string;
  description: string | null;
  category: string | null;
}

export interface ChatSession {
  id: string;
  /** Phase 2 closure fast-follow GAP #2 (2026-06-30) — short_id (chs-prefix)
   *  returned from POST /api/chat/sessions so URL navigation goes to
   *  /chat/<short_id> not the raw UUID. */
  short_id: string;
  created_at: string;
  title: string | null;
  /** Round 7 Item 36 (2026-06-25) — renamed from `model_instance_id` to match
   *  the canonical DB column `chat__session.llm_model_instance_id`. The prior
   *  SPA-side alias drifted from `ChatSessionSummary` (which already used the
   *  DB name) + the server endpoint at functions/api/chat/sessions.ts also
   *  renamed in lockstep. Callers in apps/ui/src/pages/Chat.tsx pass the
   *  DB-canonical name in `createChatSession` payloads. */
  llm_model_instance_id: string;
  permission_set_id: string | null;
  /** Push #34: manifest prefetched at session create. Empty array =
   *  tools_unavailable=true (gateway down or no assignments). */
  tools?: ChatTool[];
  tools_routing?: ChatToolRouting[];
  tools_unavailable?: boolean;
  suggested_prompts?: ChatSuggestedPrompt[];
}

export function createChatSession(payload: {
  llm_model_instance_id?: string;
  initial_prompt_instance_id?: string;
}): Promise<ChatSession> {
  return post('/api/chat/sessions', payload);
}

/**
 * SSE streaming chat send. Returns a fetch Response whose body is an
 * EventSource-compatible stream. The /chat page consumes it via ReadableStream
 * reader + TextDecoder (EventSource doesn't support POST bodies).
 *
 * Tool calls flow through olytic__dispatch_tool (D-133) — vendor microservices
 * stay stateless; the gateway adds OAuth + RLS + metering on the way through.
 */
export async function sendChatMessage(
  payload: {
    session_id: string;
    message: string;
    /** Optional knowledge prompt instance to prepend as a system message. */
    prompt_instance_id?: string;
    model_instance_id?: string;
    /** Push #34: tools prefetched at session create. Server uses them to
     *  build the Anthropic tools parameter for this turn. Empty = no tools. */
    tools?: ChatTool[];
    tools_routing?: ChatToolRouting[];
    /** Push #78 M1.75 H3 — when true, server appends Anthropic native
     *  web_search_20260209 tool to the dispatch. Composer toggle wires
     *  through. */
    enable_web_search?: boolean;
    /** Push 5-D Item 4 (2026-07-11) — when true AND the active model
     *  carries the `extended_thinking` capability token (Migration 0148
     *  seeded on Claude Sonnet 4.6 + Opus 4.6), server adds
     *  `thinking: {type:'enabled', budget_tokens:8192}` to the Anthropic
     *  request. Reasoning-delta frames stream back and the ReasoningPanel
     *  renders them live. */
    extended_thinking?: boolean;
    /** Push #82.7b — chat__message_attachment ids for files the user
     *  uploaded via /api/files/upload for this turn. Server lazy-uploads
     *  to Anthropic Files API on first reference + caches the
     *  anthropic_file_id back to the row. RLS auto-filters cross-tenant
     *  ids on the server side. */
    attachment_ids?: string[];
    /** Push 5-C-cont (2026-07-11) — Library documents attached via the
     *  composer From Library picker. See SendPayload in
     *  apps/ui/functions/api/chat/_lib/types.ts. */
    referenced_document_short_ids?: string[];
    /** Push 5-post-smoke-29 Sub-agent Q (2026-07-15) — user-explicitly-picked
     *  tool name from the composer `+` menu's "Call a tool" picker. When
     *  set, the server appends a strong "invoke this tool now" hint to
     *  the system prompt. Cleared client-side after each send. */
    force_invoked_tool?: string;
  },
  /** Push #54: AbortSignal so the Stop button in the chat composer can
   *  actually cancel the in-flight SSE stream. Wired from a useRef-held
   *  AbortController in Chat.tsx — calling controller.abort() rejects the
   *  fetch + halts the reader. The caller catches the resulting
   *  DOMException AbortError silently. */
  options?: { signal?: AbortSignal },
): Promise<Response> {
  const res = await fetch('/api/chat/stream', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify(payload),
    ...(options?.signal !== undefined ? { signal: options.signal } : {}),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const { message, detail, incidentId, code } = extractError(text, '/api/chat/stream', 'POST', res.status);
    throw new ApiError(res.status, message, detail, incidentId, code);
  }
  return res;
}

// ---- Chat history layer (Push #23 Track C) ----
//
// The chat-history left rail reads from /api/chat/sessions/list, navigates
// to /chat/:sessionId on click, and lets users rename via PATCH or soft-delete
// via DELETE on /api/chat/sessions/:id. Backed by migration 0058.

/**
 * Row shape returned by /api/chat/sessions/list + /api/chat/sessions/:id.
 *
 * Distinct from ChatSession (which is the create-response shape returned by
 * POST /api/chat/sessions and carries prefetched tools + suggested_prompts).
 * If the two shapes diverge further we'll narrow; for now ChatSessionSummary
 * is what the rail renders.
 */
export interface ChatSessionSummary {
  id: string;
  /** Sub-agent JJJJJ Track 1 (2026-07-29) — chs-prefix short_id (chat__session.short_id,
   *  Migration 0113). Optional for back-compat with older responses (list
   *  endpoint didn't return it pre-JJJJJ). The SPA prefers short_id for URL
   *  navigation (rail row click, new-chat "+"); UUID fallback still works because
   *  /chat/:short_id + all /api/chat/sessions/* endpoints dual-accept per
   *  Cluster C (2026-06-30). */
  short_id?: string;
  title: string | null;
  created_at: string;
  last_message_at: string | null;
  llm_model_instance_id: string;
  soft_compacted_at: string | null;
  /** Only present on the GET-one response, not the list. */
  auto_titled_at?: string | null;
  /** Push #51 — null = not archived, ISO timestamp = archived at.
   *  Default list endpoint filters archived rows out; only present on
   *  rows returned by the GET-one or future archived-filter view. */
  archived_at?: string | null;
  /** Push #51 — null = not pinned, ISO timestamp = pinned at.
   *  Pinned chats render in the rail's "Pinned" section above "Recent". */
  pinned_at?: string | null;
  /** Push #84 round 7 — Slack-style folders. NULL = root (rail
   *  date-groups it as before). Non-null = nested under that folder. */
  folder_id?: string | null;
  /**
   * Push #4.6 (D-187) — sharer-side indicator: true when the current user
   * owns this session AND has shared it with at least one teammate via
   * chat__share_session(mode='live'). Drives the outgoing-arrow icon on
   * the rail row title. Returned by /api/chat/sessions/list; also
   * returned by /api/chat/sessions/:id as of Push 5-post-smoke-31 so
   * InlineChatTitle can render the "Shared" pill without a second
   * round-trip.
   */
  is_shared_by_me?: boolean;
  /**
   * Push #4.6 (D-187) — recipient-side indicator: true when this session
   * belongs to someone else AND a chat__session_assignment row makes the
   * current user a recipient. Drives the incoming-arrow icon on the rail
   * row title. Also returned by /api/chat/sessions/:id as of Push
   * 5-post-smoke-31.
   */
  is_shared_with_me?: boolean;
  /**
   * Round 4 Item 3 (Migration 0118, 2026-06-25) — parent session id when
   * this row was created via chat__fork_session OR
   * chat__share_session(mode='copy'). NULL for normal sessions. Returned
   * by /api/chat/sessions/[id] GET; absent from the list endpoint.
   */
  cloned_from_session_id?: string | null;
  /**
   * Round 4 Item 3 — parent session title for the "Forked from {title}" pill
   * at the top of forked sessions. NULL when parent doesn't exist (deleted)
   * or the row isn't a fork. Returned alongside cloned_from_session_id by
   * the GET-one endpoint.
   */
  parent_session_title?: string | null;
  /**
   * Sub-agent LLLLL (2026-07-29) — parent session's chs-prefix short_id so
   * the "Forked from {title}" pill can link to /chat/<short_id> instead of
   * /chat/<uuid>. Same null semantics as parent_session_title.
   */
  parent_session_short_id?: string | null;
  /**
   * Push 5-post-smoke-1 (2026-07-11) — read-state timestamps sourced from
   * Migration 0150. The rail computes the unread indicator client-side
   * via the canonical predicate:
   *   unread =
   *     (manually_marked_unread_at IS NOT NULL AND
   *       (last_read_at IS NULL OR manually_marked_unread_at > last_read_at))
   *     OR
   *     (manually_marked_unread_at IS NULL AND last_message_at IS NOT NULL AND
   *       (last_read_at IS NULL OR last_message_at > last_read_at))
   * Both default NULL for pre-migration rows.
   */
  last_read_at?: string | null;
  manually_marked_unread_at?: string | null;
}

/** Push #84 round 7 — chat folder records. Owned per-user (Tier 3
 *  RLS). Drives the Slack-style group-into-folders behavior in the
 *  left rail. */
export interface ChatFolder {
  id: string;
  name: string;
  sort_order: number;
  created_at: string;
}
export function fetchChatFolders(): Promise<{ folders: ChatFolder[] }> {
  return get('/api/chat/folders');
}
export function createChatFolder(name: string): Promise<{ folder: ChatFolder }> {
  return post('/api/chat/folders', { name });
}
export async function updateChatFolder(
  id: string,
  patch: { name?: string; sort_order?: number },
): Promise<{ folder: { id: string; name: string; sort_order: number } }> {
  const res = await fetch(`/api/chat/folders/${encodeURIComponent(id)}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiError(res.status, text || `PUT /api/chat/folders/${id} failed`);
  }
  return res.json() as Promise<{ folder: { id: string; name: string; sort_order: number } }>;
}
export async function deleteChatFolder(id: string): Promise<{ ok: true }> {
  const res = await fetch(`/api/chat/folders/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiError(res.status, text || `DELETE /api/chat/folders/${id} failed`);
  }
  return res.json() as Promise<{ ok: true }>;
}

/**
 * Push #4.5 Item 8 — keyword search across title + folder name + recent
 * message content. When `search` is provided, the server returns sessions
 * whose title, parent folder name, or any message content matches ILIKE
 * %search%. Bare call (no arg) returns the default 200-row recency window.
 *
 * Push 63 (2026-07-16) — filter dimensions added. status / last_activity
 * / sharing are applied server-side so the 200-row cap sees the filtered
 * set (not filtered post-hoc from a mixed 200). Every dimension carries
 * an "All" option that omits the corresponding WHERE clause.
 *   - status:        active | archived | all   (default active)
 *   - last_activity: 1d | 3d | 7d | 30d | all  (default 7d)
 *   - sharing:       all | mine | shared_with_me | shared_by_me
 * Undefined / omitted opts fall back to the endpoint's defaults.
 */
export interface FetchChatSessionsOpts {
  search?: string;
  status?: 'active' | 'archived' | 'all';
  last_activity?: '1d' | '3d' | '7d' | '30d' | 'all';
  sharing?: 'all' | 'mine' | 'shared_with_me' | 'shared_by_me';
}
export function fetchChatSessions(
  opts?: FetchChatSessionsOpts,
): Promise<{ sessions: ChatSessionSummary[] }> {
  const params = new URLSearchParams();
  if (opts?.search !== undefined && opts.search.trim() !== '') {
    params.set('search', opts.search.trim());
  }
  if (opts?.status !== undefined) params.set('status', opts.status);
  if (opts?.last_activity !== undefined) params.set('last_activity', opts.last_activity);
  if (opts?.sharing !== undefined) params.set('sharing', opts.sharing);
  const qs = params.toString();
  return get(`/api/chat/sessions/list${qs === '' ? '' : `?${qs}`}`);
}

/**
 * Push 50 Sub-agent LL (2026-07-15) — Track 2 grouped chat search.
 *
 * Hits GET /api/chat/search?q=<query> and returns three parallel arrays
 * so the chat rail can render dedicated Folders / Chats / Messages
 * sections instead of squishing everything through the sessions bucket
 * and losing the "which record actually matched" signal.
 *
 * Empty / < 2 char queries resolve to empty arrays server-side; caller
 * still typically gates the fetch behind a debounce + length check to
 * keep the request rate sane.
 */
export interface ChatSearchFolderHit {
  id: string;
  short_id: string;
  name: string;
}
export interface ChatSearchSessionHit {
  id: string;
  short_id: string;
  title: string | null;
  folder_id: string | null;
  last_message_at: string | null;
  created_at: string;
}
export interface ChatSearchMessageHit {
  id: string;
  short_id: string;
  session_id: string;
  session_short_id: string;
  session_title: string | null;
  role: string;
  snippet: string;
  created_at: string;
}
export interface ChatSearchResults {
  folders: ChatSearchFolderHit[];
  sessions: ChatSearchSessionHit[];
  messages: ChatSearchMessageHit[];
}
export function searchChat(query: string): Promise<ChatSearchResults> {
  return get(`/api/chat/search?q=${encodeURIComponent(query.trim())}`);
}

export function fetchChatSession(id: string): Promise<ChatSessionSummary> {
  return get(`/api/chat/sessions/${encodeURIComponent(id)}`);
}

/**
 * Push #23 / M1 P1.5 — Restore conversation history on session switch.
 * Returns messages ordered by created_at ASC (oldest first).
 */
export interface ChatMessageAttachmentRef {
  id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
}
export interface ChatMessageHistory {
  id: string;
  /** Push 5-post-smoke-15 (2026-07-14) — chm-prefix short_id from
   *  Migration 0123. Fed to /api/chat/messages/:short_id/pin so pins
   *  survive across devices. */
  short_id: string;
  role: string;
  content: string;
  tool_calls: unknown;
  reasoning_payload: unknown;
  input_tokens: number;
  output_tokens: number;
  cost_credits_charged: string | null;
  created_at: string;
  /** Push 5-post-smoke-15 (2026-07-14) — Migration 0153 boolean. True
   *  when the session owner clicked Pin on this message. */
  is_pinned: boolean;
  /** Push #82.8 — per-message attachments (chat__message_attachment rows
   *  joined in the messages endpoint). Empty array when none. Drives the
   *  inline chip rendered below user bubbles + the session attachments
   *  rail card. */
  attachments: ChatMessageAttachmentRef[];
}
export function fetchChatSessionMessages(id: string): Promise<{ messages: ChatMessageHistory[] }> {
  return get(`/api/chat/sessions/${encodeURIComponent(id)}/messages`);
}

export async function updateChatSession(
  id: string,
  patch: {
    title?: string | null;
    /** Push #51 — true = archive, false = unarchive. */
    archived?: boolean;
    /** Push #51 — true = pin, false = unpin. */
    pinned?: boolean;
    /** Push #84 round 7 — null = move to root; uuid = nest under folder. */
    folder_id?: string | null;
    /** Push 5-post-smoke-1 (2026-07-11): true = stamp last_read_at + clear
     *  manually_marked_unread_at (session-open auto-read). */
    mark_read?: boolean;
    /** Push 5-post-smoke-1 (2026-07-11): true = stamp
     *  manually_marked_unread_at (row menu "Mark as unread");
     *  false = clear the stamp ("Mark as read" from menu when currently
     *  marked unread). */
    mark_unread?: boolean;
  },
): Promise<ChatSessionSummary> {
  const res = await fetch(`/api/chat/sessions/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const { message, detail, incidentId } = extractError(
      text,
      `/api/chat/sessions/${id}`,
      'PATCH',
      res.status,
    );
    throw new ApiError(res.status, message, detail, incidentId);
  }
  return (await res.json()) as ChatSessionSummary;
}

// Push #78 round 16 (item D) — truncate-from-here for regenerate +
// edit-and-resubmit. Deletes the target message and every newer message
// in the session. Caller chooses what to do next (re-fire dispatch for
// regenerate, focus composer for edit).
export async function truncateChatFromMessage(
  sessionId: string,
  messageId: string,
): Promise<{ ok: true; deleted: number }> {
  return post<{ ok: true; deleted: number }>(
    `/api/chat/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}/truncate-from-here`,
    {},
  );
}

export async function deleteChatSession(id: string): Promise<{ ok: true }> {
  const res = await fetch(`/api/chat/sessions/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const { message, detail, incidentId } = extractError(
      text,
      `/api/chat/sessions/${id}`,
      'DELETE',
      res.status,
    );
    throw new ApiError(res.status, message, detail, incidentId);
  }
  return (await res.json()) as { ok: true };
}

// ---- D-187 chat session sharing (Push #4.6 Item 1) -------------------
//
// Single SPA-side wrapper for the POST /api/chat/sessions/[id]/share
// endpoint, which itself wraps the chat__share_session SECDEF fn from
// Migration 0104. Two modes:
//   - 'live' — recipient sees the session in their rail and reads it
//              real-time (chat__session_select RLS widened in 0104).
//   - 'copy' — deep-clone session + messages + attachments to a new
//              row owned by the recipient.
//
// Errors surface through ApiError; the SECDEF fn's message is preserved
// in `friendly` so callers can render "{name} has disabled inbound
// sharing" etc. verbatim from the server.

export interface ShareChatSessionLiveResult {
  mode: 'live';
  junction_id: string;
  chat_session_id: string;
}

export interface ShareChatSessionCopyResult {
  mode: 'copy';
  new_session_id: string;
  cloned_from_session_id: string;
  messages_cloned?: number;
  attachments_cloned?: number;
}

export type ShareChatSessionResult =
  | ShareChatSessionLiveResult
  | ShareChatSessionCopyResult;

export function shareChatSession(
  sessionId: string,
  recipientAccountUserId: string,
  mode: 'live' | 'copy',
): Promise<ShareChatSessionResult> {
  return post<ShareChatSessionResult>(
    `/api/chat/sessions/${encodeURIComponent(sessionId)}/share`,
    {
      recipient_account_user_id: recipientAccountUserId,
      mode,
    },
  );
}

// ---- Round 4 Item 3: Fork-a-chat (Anthropic-style branching) ----
// Calls workspace_selfservice.chat__fork_session SECDEF fn shipped by Migration
// 0118. Deep-clones the session + messages chronologically up to AND including
// the fork-point message (attachments tied to cloned messages or session-level
// come along). The new session lands owned by the caller with
// cloned_from_session_id = parent (re-uses D-187 column from Migration 0104).
// Only the session creator can fork their own session (same actor-is-creator
// gate as share). Errors surface via ApiError with friendly server messages.

export interface ForkChatSessionResult {
  new_session_id: string;
  /** Sub-agent LLLLL (2026-07-29) — chat__session.short_id (chs-prefix)
   *  fetched by fork.ts in the same withScope tx so callers can navigate
   *  to /chat/<short_id> instead of /chat/<uuid>. Null only when the row
   *  is somehow missing a short_id (all rows should carry one post-
   *  Migration 0117; the null branch is defensive). */
  new_session_short_id: string | null;
  cloned_from_session_id: string;
  fork_message_id: string;
  messages_cloned: number;
  attachments_cloned: number;
}

export function forkChatSession(
  sessionId: string,
  forkMessageId: string,
): Promise<ForkChatSessionResult> {
  return post<ForkChatSessionResult>(
    `/api/chat/sessions/${encodeURIComponent(sessionId)}/fork`,
    {
      fork_message_id: forkMessageId,
    },
  );
}
